import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Radios, Button, Details, ErrorSummary } from 'govuk-react-jsx';
import { useSelector, useDispatch } from 'react-redux';
import {
  getLandlordOrPmcPrivateParking,
  setLandlordOrPmcPrivateParking,
} from '../../features/application/applicationJourneySlice';
import {
  getCurrentLanguage,
  getCopyProviderCY,
  getCopyProviderEN,
} from '../../features/app/appSlice';
import { getUserDetails } from '../../features/dashboard/dashboardSlice';
import useTitle from '../../hooks/useTitle';

const LandlordOrPmcPrivateParking = ({ location }) => {
  const isFromCheckAnswers = location?.state?.isFromCheckAnswers;
  const [errorMsg, setErrorMsg] = useState(null);
  const [errorList, setErrorList] = useState([]);
  const privateParkingRedux = useSelector(getLandlordOrPmcPrivateParking);
  const currentLanguage = useSelector(getCurrentLanguage);
  const copyProviderCY = useSelector(getCopyProviderCY);
  const copyProviderEN = useSelector(getCopyProviderEN);
  const currentUserData = useSelector(getUserDetails);
  const copyProvider =
    currentLanguage === 'en' ? copyProviderEN : copyProviderCY;
  const dispatch = useDispatch();
  useTitle(
    'landlordApp.LandlordOrPmcPrivateParking.s15',
    'Does this property have private parking? - OZEV Electric Vehicle Chargepoint Grant - GOV.UK'
  );

  const history = useHistory();

  // Error message
  const radioError = copyProvider.getCopy(
    'landlordApp.LandlordOrPmcPrivateParking.s5',
    'Make a selection'
  );

  // Handle radio button selection
  const handleRadioButtonClick = (event) => {
    dispatch(setLandlordOrPmcPrivateParking(event.target.value));
  };

  // Handle button click
  const handleButtonClick = () => {
    const showRadioErrorMsg = {
      children: radioError,
    };

    if (!privateParkingRedux) {
      setErrorMsg(showRadioErrorMsg);
      setErrorList([{ ...showRadioErrorMsg, href: '#yes' }]);
    } else if (privateParkingRedux === 'yes' && isFromCheckAnswers) {
      history.push('/landlordApp/check-your-answers');
    } else if (privateParkingRedux === 'yes') {
      history.push('/landlordApp/declaration');
    } else {
      history.push('/landlordApp/unable-to-proceed');
    }
  };

  const pageModel = (
    <>
      {errorList?.length > 0 && (
        <ErrorSummary
          errorList={errorList}
          titleChildren={copyProvider.getCopy(
            'Common.error-summery',
            'There is a problem'
          )}
        />
      )}
      <span className="govuk-caption-l">
        {currentUserData != null &&
          currentUserData.business_name != null &&
          currentUserData.business_name}
      </span>
      <h1 className="govuk-heading-xl">
        {copyProvider.getCopy(
          'landlordApp.LandlordOrPmcPrivateParking.s1',
          'Does this property have private parking?'
        )}
      </h1>
      <Radios
        errorMessage={errorMsg || null}
        onChange={handleRadioButtonClick}
        idPrefix="privateParking"
        items={[
          {
            children: copyProvider.getCopy(
              'landlordApp.LandlordOrPmcPrivateParking.s2',
              'Yes'
            ),
            value: 'yes',
            id: 'yes',
          },
          {
            children: copyProvider.getCopy(
              'landlordApp.LandlordOrPmcPrivateParking.s3',
              'No'
            ),
            value: 'no',
            id: 'no',
          },
        ]}
        name="private-parking"
        value={privateParkingRedux}
      />

      <Details
        summaryChildren={copyProvider.getCopy(
          'landlordApp.LandlordOrPmcPrivateParking.s6',
          'What is private parking?'
        )}
      >
        <p>
          {copyProvider.getCopy(
            'landlordApp.LandlordOrPmcPrivateParking.s7',
            'The address where you’re applying to install chargepoints must have private parking. You must own the parking space or have sole legal right to it.'
          )}
        </p>
        <p>
          {copyProvider.getCopy(
            'landlordApp.LandlordOrPmcPrivateParking.s8',
            'The private parking may be connected the property or separate from it.'
          )}
        </p>
        <p>
          {copyProvider.getCopy(
            'landlordApp.LandlordOrPmcPrivateParking.s9',
            'You must be able to show you have a legal entitlement to the parking space if it is separate from the property. You may prove this through the provision of Land Registry title deeds.'
          )}
        </p>
        <p>
          {copyProvider.getCopy(
            'landlordApp.LandlordOrPmcPrivateParking.s10',
            'If you do not own the parking space or have sole legal right to it you cannot apply for the grant. This includes:'
          )}
        </p>
        <ul>
          <li>
            {copyProvider.getCopy(
              'landlordApp.LandlordOrPmcPrivateParking.s11',
              'rented spaces'
            )}
          </li>
          <li>
            {copyProvider.getCopy(
              'landlordApp.LandlordOrPmcPrivateParking.s12',
              'spaces shared with other properties'
            )}
          </li>
          <li>
            {copyProvider.getCopy(
              'landlordApp.LandlordOrPmcPrivateParking.s13',
              'spaces that provide access routes to other properties'
            )}
          </li>
        </ul>
        <p>
          {copyProvider.getCopy(
            'landlordApp.LandlordOrPmcPrivateParking.s14',
            'Cables are not permitted over public land such as pavements. This is true even if the arrangement is temporary.'
          )}
        </p>
      </Details>
      <Button onClick={handleButtonClick}>
        {copyProvider.getCopy(
          'landlordApp.LandlordOrPmcPrivateParking.s4',
          'Continue'
        )}
      </Button>
    </>
  );
  return pageModel;
};

LandlordOrPmcPrivateParking.propTypes = {
  location: PropTypes.shape({
    state: PropTypes.shape({
      isFromCheckAnswers: PropTypes.bool,
    }),
  }),
};

LandlordOrPmcPrivateParking.defaultProps = {
  location: {
    state: {
      isFromCheckAnswers: false,
    },
  },
};

export default LandlordOrPmcPrivateParking;
